import L from 'leaflet';
import {
  DEFAULT_ACTIVITY_TIMEZONE,
  formatWallClockHm,
  wallClockInTimeZone,
} from '../../utils/activityDateTime';
import type { BoardEmployee } from './plantafelTypes';

function isSameWallClockDay(a: Date, b: Date, timeZone: string): boolean {
  const wa = wallClockInTimeZone(a, timeZone);
  const wb = wallClockInTimeZone(b, timeZone);
  return wa.y === wb.y && wa.mo === wb.mo && wa.d === wb.d;
}

export function formatAppointmentTimeRange(
  dueAt: string | null,
  endAt: string | null,
  timeZone: string = DEFAULT_ACTIVITY_TIMEZONE,
): string {
  if (!dueAt) return '';
  const start = new Date(dueAt);
  const end = endAt ? new Date(endAt) : new Date(start.getTime() + 60 * 60 * 1000);
  const timeFmt = (d: Date) => formatWallClockHm(d, timeZone);
  if (isSameWallClockDay(start, end, timeZone)) {
    return `${timeFmt(start)} – ${timeFmt(end)}`;
  }
  const dateFmt = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', timeZone });
  return `${dateFmt.format(start)} ${timeFmt(start)} – ${dateFmt.format(end)} ${timeFmt(end)}`;
}

export function formatMapSubtitleDate(d: Date): string {
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}

export function createAppointmentIcon(options: {
  employee: BoardEmployee | null;
  initials: string;
  color: string;
  timeLabel: string;
  extraAssigneeCount?: number;
}): L.DivIcon {
  const { initials, color, timeLabel, extraAssigneeCount = 0 } = options;
  const extra =
    extraAssigneeCount > 0
      ? `<span style="position:absolute;right:-4px;bottom:-4px;background:#64748b;color:#fff;font-size:10px;font-weight:700;line-height:1;border-radius:9999px;padding:2px 5px;border:2px solid #fff;">+${extraAssigneeCount}</span>`
      : '';

  const html = `
    <div class="appointment-marker-pin" style="display:flex;align-items:center;gap:8px;transform:translate(-18px,-18px);pointer-events:auto;cursor:pointer;">
      <div style="position:relative;flex-shrink:0;">
        <div style="width:36px;height:36px;border-radius:9999px;background:${color};border:2.5px solid #fff;box-shadow:0 2px 8px rgba(15,23,42,.2);display:flex;align-items:center;justify-content:center;color:#fff;font-family:inherit;font-size:12px;font-weight:700;line-height:1;letter-spacing:-0.02em;">
          ${escapeHtml(initials)}
        </div>
        ${extra}
      </div>
      <div style="background:var(--color-app-surface);border-radius:8px;padding:5px 12px;font-family:inherit;font-size:14px;font-weight:600;font-variant-numeric:tabular-nums;color:var(--color-app-text);white-space:nowrap;box-shadow:0 2px 10px rgba(15,23,42,.12);border:1px solid var(--color-app-border);">
        ${escapeHtml(timeLabel)}
      </div>
    </div>
  `;

  return L.divIcon({
    html,
    className: 'appointment-map-marker',
    iconSize: [1, 1],
    iconAnchor: [0, 0],
  });
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Slight offset for markers sharing the same coordinates. */
export function offsetLatLng(lat: number, lng: number, index: number, total: number): [number, number] {
  if (total <= 1 || index === 0) return [lat, lng];
  const angle = (index / total) * Math.PI * 2;
  const radius = 0.00015 * Math.min(index + 1, 4);
  return [lat + Math.sin(angle) * radius, lng + Math.cos(angle) * radius];
}

/** Carto Voyager basemap (requires CARTO_BASEMAPS_API_KEY from Doppler). */
const cartoBasemapsApiKey =
  typeof __CARTO_BASEMAPS_API_KEY__ !== 'undefined' ? __CARTO_BASEMAPS_API_KEY__.trim() : '';
export const MAP_TILE_URL = cartoBasemapsApiKey
  ? `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${encodeURIComponent(cartoBasemapsApiKey)}`
  : 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png';
export const MAP_TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';

export const MAP_FALLBACK_COUNTRY_CENTER: [number, number] = [51.1657, 10.4515];
export const MAP_FALLBACK_COUNTRY_ZOOM = 6;
export const MAP_FALLBACK_COMPANY_ZOOM = 12;
export const MAP_SINGLE_MARKER_ZOOM = 13;

export type MapDefaultCenter = {
  lat: number;
  lng: number;
  source: 'company' | 'country';
};
